import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError, shouldRetryRequest } from '@opengoal/http';

/**
 * 402 means the agency's subscription is blocked and only /billing answers.
 * The route guard catches this on navigation from the cached profile; this
 * catches it when the state changes under an open session (a renewal fails
 * while the tab is open). Same hard reset as the 401 handler, for the same
 * reason: every in-flight query would fail the same way.
 */
function onPaymentRequired(error: unknown): void {
  if (
    error instanceof ApiError &&
    error.status === 402 &&
    !window.location.pathname.startsWith('/billing')
  ) {
    window.location.assign('/billing');
  }
}

/**
 * The app's server-state cache.
 *
 * TanStack Query owns everything that comes from the API. Zustand owns local UI
 * preferences (theme, sidebar) and nothing else — the moment server data is
 * copied into a zustand store it starts going stale, and reconciling the two is
 * a class of bug this split avoids entirely.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onPaymentRequired }),
  mutationCache: new MutationCache({ onError: onPaymentRequired }),
  defaultOptions: {
    queries: {
      // Admin data is read far more often than it changes. 30s of staleness
      // removes most redundant refetches while keeping lists feeling live.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetryRequest,
      // Refetching on every window focus is noisy in a tool people leave open
      // all day next to their email.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      // A failed write must never be retried automatically — at this layer we
      // cannot tell a create from a retry, and duplicating a deal or an
      // invitation is worse than showing an error.
      retry: false,
    },
  },
});
